import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { isGatewayProvider, modelRouteKey, type ChatModel } from '@reflex/shared';
import {
  getModelIconConfig,
  getModelRowSurfaceClass,
  getProviderIconConfig,
  MODEL_MAP,
  PROVIDER_LABELS,
  SELECTABLE_MODELS,
  type ModelConfig,
} from '../../lib/model-icons';
import { Button } from '../ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  popoverBoundedMaxWidthClass,
} from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command';
import { ModelProviderIcon } from './ModelProviderIcon';
import { ModelFallbackRow, ModelRichRow } from './ModelPickerRow';
import { ModelPickerTriggerContent } from './ModelPickerTriggerContent';

export type ModelPickerTriggerVariant = 'header' | 'compact' | 'compact-ghost' | 'form';

export interface ModelPickerOption {
  /** Model id stored on the config (e.g. `sonnet`, `claude-sonnet-4-20250514`). */
  id: string;
  /** Optional human label; falls back to the catalog name or id. */
  name?: string;
  /** Provider id for grouping when the id is not in the chat catalog. */
  providerId?: string;
  /**
   * When any option carries this flag, the list defaults to recommended rows
   * (plus the current selection). Chat/flow generate omit it and show all.
   */
  recommended?: boolean;
  disabled?: boolean;
  unavailableReason?: string;
}

export interface ModelPickerProps {
  /** Currently-selected model id, or undefined when inheriting the default. */
  value?: string;
  /** Called with the new model id, or `undefined` when cleared to the default. */
  onChange: (model: string | undefined) => void;
  /**
   * Selectable models. Defaults to the shared chat-model catalog
   * (`SELECTABLE_MODELS`). Callers can pass an agent-type-specific list.
   */
  options?: ModelPickerOption[];
  /** When set, only catalog models in this set are listed (chat enablement). */
  enabledModelIds?: Set<string>;
  /** Show a clear-to-default row. Defaults to false; ModelCombobox opts in. */
  allowInherit?: boolean;
  /** Accept a free-form typed id. Defaults to false; ModelCombobox opts in. */
  allowCustom?: boolean;
  /** Trigger placeholder when nothing is selected. */
  placeholder?: string;
  /** Label for the clear-to-default item. */
  defaultLabel?: string;
  /** Search input placeholder. */
  searchPlaceholder?: string;
  /**
   * Trigger chrome. `header` / `compact` / `compact-ghost` match chat;
   * `form` is the full-width outline field used in config panels.
   */
  triggerVariant?: ModelPickerTriggerVariant;
  /** Optional footer below the list (e.g. Manage models link). */
  footer?: ReactNode;
  /** Optional empty-state CTA when no models are available. */
  emptyCta?: ReactNode;
  /**
   * Prefer this provider's group first when grouping by `providerId`
   * (agent settings). Gateway providers still trail.
   */
  preferredProviderId?: string | null;
  /**
   * Manifest-ordered provider ids used as a tiebreak when collapsing
   * direct vs gateway routes of the same logical model.
   */
  providerOrder?: readonly string[];
  /**
   * Optional content stacked above the model list (e.g. provider picker).
   * Selecting inside these sections should not close the outer popover.
   */
  leadingSection?: ReactNode;
  /** Optional content stacked below the model list (e.g. endpoint picker). */
  trailingSection?: ReactNode;
  /** Called after a model is chosen (standalone picker closes first). */
  onClose?: () => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
  /**
   * DOM id on the trigger button. Pair with an `InspectorField`'s `htmlFor`
   * (rather than letting the field wrap this trigger in a `<label>`) so the
   * caption is programmatically associated with the control instead of
   * being an ambiguous click target next to it.
   */
  id?: string;
  listTutorialId?: string;
  compactList?: boolean;
}

const DEFAULT_OPTIONS: ModelPickerOption[] = SELECTABLE_MODELS.map((m) => ({
  id: m.id,
  name: m.name,
}));

const GROUP_HEADING_CLASS =
  '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider';

const PICKER_ITEM_CLASS = 'flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-1.5';

function pickerItemClass(bgColor: string | undefined, selected: boolean, extra?: string): string {
  return cn(PICKER_ITEM_CLASS, getModelRowSurfaceClass(bgColor, selected), extra);
}

function optionRowBgColor(option: ModelPickerOption, cfg?: ModelConfig): string {
  return cfg?.bgColor ?? getModelIconConfig(option.id).bgColor;
}

function resolveConfig(id: string): ModelConfig | undefined {
  return MODEL_MAP.get(id as ChatModel);
}

/** Derive a provider group key from an option. */
export function resolveOptionProviderId(option: ModelPickerOption): string {
  if (option.providerId) return option.providerId;
  const catalog = resolveConfig(option.id);
  if (catalog) return catalog.provider;
  const slash = option.id.indexOf('/');
  if (slash > 0) return option.id.slice(0, slash);
  return 'other';
}

/** Trim a redundant `provider/` prefix from a label under that heading. */
export function trimProviderPrefix(label: string, providerId: string): string {
  const prefix = `${providerId}/`;
  if (label.startsWith(prefix)) return label.slice(prefix.length);
  return label;
}

interface OptionGroup {
  provider: string;
  label: string;
  options: ModelPickerOption[];
}

function groupOptionsByProvider(
  options: ModelPickerOption[],
  preferredProviderId?: string | null,
): OptionGroup[] {
  const groups: OptionGroup[] = [];
  const indexByProvider = new Map<string, number>();
  for (const o of options) {
    const provider = resolveOptionProviderId(o);
    let idx = indexByProvider.get(provider);
    if (idx === undefined) {
      idx = groups.length;
      indexByProvider.set(provider, idx);
      groups.push({
        provider,
        label: PROVIDER_LABELS[provider] ?? (provider === 'other' ? 'Other' : provider),
        options: [],
      });
    }
    groups[idx]!.options.push(o);
  }

  const preferred = preferredProviderId ?? null;
  return groups.sort((a, b) => {
    const aGateway = isGatewayProvider(a.provider) ? 1 : 0;
    const bGateway = isGatewayProvider(b.provider) ? 1 : 0;
    if (aGateway !== bGateway) return aGateway - bGateway;
    if (preferred) {
      if (a.provider === preferred && b.provider !== preferred) return -1;
      if (b.provider === preferred && a.provider !== preferred) return 1;
    }
    return 0;
  });
}

export interface ModelRouteGroup {
  key: string;
  primary: ModelPickerOption;
  alternates: ModelPickerOption[];
}

/**
 * Collapse transport routes of the same logical model into one group.
 * Primary wins by: currently selected → non-gateway → earlier in
 * `providerOrder` → `recommended` → first seen. List order follows the
 * first appearance of each group's primary.
 */
export function collapseModelRoutes(
  options: ModelPickerOption[],
  opts: { selectedId?: string; providerOrder?: readonly string[] } = {},
): ModelRouteGroup[] {
  const { selectedId, providerOrder } = opts;
  const orderIndex = new Map((providerOrder ?? []).map((id, i) => [id, i]));

  function providerRank(option: ModelPickerOption): number {
    const provider = resolveOptionProviderId(option);
    return orderIndex.get(provider) ?? Number.MAX_SAFE_INTEGER;
  }

  function isBetterPrimary(candidate: ModelPickerOption, current: ModelPickerOption): boolean {
    if (selectedId) {
      if (candidate.id === selectedId && current.id !== selectedId) return true;
      if (current.id === selectedId && candidate.id !== selectedId) return false;
    }
    const candidateGateway = isGatewayProvider(resolveOptionProviderId(candidate));
    const currentGateway = isGatewayProvider(resolveOptionProviderId(current));
    if (candidateGateway !== currentGateway) return !candidateGateway;
    const candidateRank = providerRank(candidate);
    const currentRank = providerRank(current);
    if (candidateRank !== currentRank) return candidateRank < currentRank;
    if (Boolean(candidate.recommended) !== Boolean(current.recommended)) {
      return Boolean(candidate.recommended);
    }
    return false;
  }

  const byKey = new Map<string, ModelPickerOption[]>();
  const keyOrder: string[] = [];
  for (const option of options) {
    const key = modelRouteKey(option.id);
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = [];
      byKey.set(key, bucket);
      keyOrder.push(key);
    }
    bucket.push(option);
  }

  return keyOrder.map((key) => {
    const bucket = byKey.get(key)!;
    let primary = bucket[0]!;
    for (let i = 1; i < bucket.length; i++) {
      const candidate = bucket[i]!;
      if (isBetterPrimary(candidate, primary)) primary = candidate;
    }
    return {
      key,
      primary,
      alternates: bucket.filter((o) => o.id !== primary.id),
    };
  });
}

function providerDisplayLabel(option: ModelPickerOption): string {
  const provider = resolveOptionProviderId(option);
  return PROVIDER_LABELS[provider] ?? (provider === 'other' ? 'Other' : provider);
}

export interface ModelPickerListProps {
  value?: string;
  onChange: (model: string | undefined) => void;
  options?: ModelPickerOption[];
  enabledModelIds?: Set<string>;
  allowInherit?: boolean;
  allowCustom?: boolean;
  defaultLabel?: string;
  searchPlaceholder?: string;
  footer?: ReactNode;
  emptyCta?: ReactNode;
  preferredProviderId?: string | null;
  /**
   * Manifest-ordered provider ids used as a tiebreak when collapsing
   * direct vs gateway routes of the same logical model.
   */
  providerOrder?: readonly string[];
  leadingSection?: ReactNode;
  trailingSection?: ReactNode;
  compactList?: boolean;
  /** Disable search and option rows (empty policy-filtered catalog). */
  disabled?: boolean;
  /**
   * Size the list to the remaining popover height so the header and footer
   * stay visible and only the command list scrolls.
   */
  fillAvailable?: boolean;
  listTutorialId?: string;
  /** Controlled open/query reset when the parent popover closes. */
  resetKey?: number | string | boolean;
}

function optionMatchesQuery(option: ModelPickerOption, q: string): boolean {
  return (
    option.id.toLowerCase().includes(q) ||
    (option.name ?? '').toLowerCase().includes(q) ||
    (resolveConfig(option.id)?.description ?? '').toLowerCase().includes(q) ||
    (resolveConfig(option.id)?.badge ?? '').toLowerCase().includes(q) ||
    providerDisplayLabel(option).toLowerCase().includes(q)
  );
}

function routeGroupIsRecommended(group: ModelRouteGroup): boolean {
  return Boolean(group.primary.recommended) || group.alternates.some((a) => a.recommended);
}

/** Compact pill label for the active route. Gateways use the short brand name. */
function providerPillLabel(option: ModelPickerOption): string {
  const provider = resolveOptionProviderId(option);
  if (isGatewayProvider(provider)) return getProviderIconConfig(provider).shortName;
  return PROVIDER_LABELS[provider] ?? getProviderIconConfig(provider).shortName;
}

function ModelRoutePill({
  routes,
  activeId,
  onSelectRoute,
  disabled = false,
}: {
  routes: ModelPickerOption[];
  activeId: string;
  onSelectRoute: (id: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const commandRef = useRef<HTMLDivElement>(null);
  const active = routes.find((r) => r.id === activeId) ?? routes[0]!;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`model-route-pill-${modelRouteKey(active.id)}`}
          aria-label={`Route: ${providerDisplayLabel(active)}. Change route.`}
          aria-expanded={open}
          className={cn(
            'inline-flex shrink-0 items-center gap-0.5 rounded-md bg-muted px-1.5 py-0.5',
            'text-[10px] font-medium leading-none text-muted-foreground',
            'hover:bg-accent hover:text-foreground',
            'disabled:pointer-events-none disabled:opacity-50',
          )}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onPointerDown={(event) => {
            // Keep cmdk from treating the pill as a row select, but do not
            // preventDefault: Radix needs the pointer event to open.
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Tab') event.stopPropagation();
          }}
        >
          <span className="max-w-20 truncate">{providerPillLabel(active)}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          // Sit above the model editor (`z-[60]`) so the route menu is not
          // buried under the list that owns this pill.
          'z-[70] w-48 overflow-hidden p-0',
          popoverBoundedMaxWidthClass('14rem'),
        )}
        align="end"
        side="bottom"
        sideOffset={4}
        data-testid="model-route-menu"
        onOpenAutoFocus={(event) => {
          // Radix's default autofocus can't find a tabbable element inside
          // (cmdk items aren't tab stops), so it would leave focus on the
          // pill and arrow keys would keep driving the outer row list.
          // Focus the inner Command root directly instead, which cmdk
          // itself listens on for arrow/Enter navigation; closing the
          // popover restores focus to the pill via Radix's default
          // onCloseAutoFocus (left untouched, above).
          event.preventDefault();
          commandRef.current?.focus();
        }}
        // cmdk triggers selection from a DOM `onClick` on each item, and
        // React bubbles synthetic events along the component tree rather
        // than the portaled DOM tree, so an unstopped click here would also
        // fire the outer row's onSelect (re-selecting the active route).
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Command ref={commandRef} shouldFilter={false}>
          <CommandList className="max-h-48">
            <CommandGroup heading="Route">
              {routes.map((route) => {
                const selected = route.id === activeId;
                return (
                  <CommandItem
                    key={route.id}
                    value={`${providerDisplayLabel(route)} ${route.id}`}
                    disabled={route.disabled}
                    data-testid={`model-route-option-${route.id}`}
                    onSelect={() => {
                      if (!route.disabled) {
                        onSelectRoute(route.id);
                        setOpen(false);
                      }
                    }}
                    className={cn('gap-2', getModelRowSurfaceClass(undefined, selected))}
                  >
                    <span className="flex-1 truncate text-sm">{providerDisplayLabel(route)}</span>
                    {route.disabled && route.unavailableReason ? (
                      <span className="shrink-0 text-2xs text-muted-foreground">
                        ({route.unavailableReason})
                      </span>
                    ) : selected ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * One logical model. Alternate transports stay on a trailing route pill so
 * the catalog stays one row per model.
 */
function ModelRouteRow({
  group,
  value,
  onSelect,
  disabled = false,
}: {
  group: ModelRouteGroup;
  value?: string;
  onSelect: (id: string) => void;
  disabled?: boolean;
}) {
  const routes = useMemo(() => [group.primary, ...group.alternates], [group]);
  const active = (value ? routes.find((r) => r.id === value) : undefined) ?? group.primary;
  const cfg = resolveConfig(active.id);
  const selected = routes.some((r) => r.id === value);
  const provider = resolveOptionProviderId(active);
  const rawName = active.name ?? cfg?.name ?? active.id;
  const displayName = trimProviderPrefix(rawName, provider);
  const multiRoute = group.alternates.length > 0;
  // `disabled` on the row applies `pointer-events-none` to the whole item,
  // including the nested route pill. When another route is still enabled,
  // keep the row (and its pill) reachable; only block selecting the active,
  // disabled route (see the `onSelect` guard below).
  const hasEnabledAlternate = multiRoute && routes.some((r) => !r.disabled);

  return (
    <CommandItem
      value={`${rawName} ${active.id} ${routes.map((r) => providerDisplayLabel(r)).join(' ')}`}
      disabled={disabled || (active.disabled && !hasEnabledAlternate)}
      onSelect={() => {
        if (!active.disabled) onSelect(active.id);
      }}
      data-testid={`model-combobox-option-${active.id}`}
      className={pickerItemClass(optionRowBgColor(active, cfg), selected)}
    >
      {cfg ? (
        <ModelRichRow
          model={cfg}
          name={active.name}
          selected={selected && active.id === value}
          muteBadge
        />
      ) : (
        <ModelFallbackRow
          id={active.id}
          name={displayName}
          title={active.id}
          selected={selected && active.id === value}
          unavailableReason={active.unavailableReason}
        />
      )}
      {multiRoute ? (
        <ModelRoutePill
          routes={routes}
          activeId={active.id}
          onSelectRoute={onSelect}
          disabled={disabled}
        />
      ) : null}
    </CommandItem>
  );
}

/**
 * Searchable, provider-grouped model list. When any option is `recommended`,
 * defaults to recommended rows plus the current selection, with a Show all
 * expander; search always matches the full (enabled) catalog. Direct vs
 * gateway routes of the same logical model collapse to one row.
 */
export function ModelPickerList({
  value,
  onChange,
  options = DEFAULT_OPTIONS,
  enabledModelIds,
  allowInherit = false,
  allowCustom = false,
  defaultLabel = 'Default (inherit)',
  searchPlaceholder = 'Search models…',
  footer,
  emptyCta,
  preferredProviderId,
  providerOrder,
  leadingSection,
  trailingSection,
  compactList = false,
  disabled = false,
  fillAvailable = false,
  listTutorialId,
  resetKey,
}: ModelPickerListProps) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  // Reset ephemeral list state when the parent popover closes/reopens.
  useEffect(() => {
    setQuery('');
    setShowAll(false);
  }, [resetKey]);

  const catalogOptions = useMemo(() => {
    let list = options;
    if (enabledModelIds) {
      list = list.filter((o) => enabledModelIds.has(o.id));
    }
    return list;
  }, [options, enabledModelIds]);

  const hasRecommended = useMemo(() => catalogOptions.some((o) => o.recommended), [catalogOptions]);

  const collapseOpts = useMemo(
    () => ({ selectedId: value, providerOrder }),
    [value, providerOrder],
  );

  const catalogRouteGroups = useMemo(
    () => collapseModelRoutes(catalogOptions, collapseOpts),
    [catalogOptions, collapseOpts],
  );

  const defaultRouteGroups = useMemo(() => {
    if (!hasRecommended) return catalogRouteGroups;
    return catalogRouteGroups.filter((g) => {
      if (routeGroupIsRecommended(g)) return true;
      if (!value) return false;
      return g.primary.id === value || g.alternates.some((a) => a.id === value);
    });
  }, [catalogRouteGroups, hasRecommended, value]);

  const normalizedQuery = query.trim();
  const searching = normalizedQuery.length > 0;
  const flatRecommended = hasRecommended && !showAll && !searching;

  const visibleRouteGroups = useMemo(() => {
    if (!searching) {
      return flatRecommended ? defaultRouteGroups : catalogRouteGroups;
    }
    const q = normalizedQuery.toLowerCase();
    return catalogRouteGroups.filter(
      (g) => optionMatchesQuery(g.primary, q) || g.alternates.some((a) => optionMatchesQuery(a, q)),
    );
  }, [searching, flatRecommended, defaultRouteGroups, catalogRouteGroups, normalizedQuery]);

  const groups = useMemo(() => {
    if (flatRecommended) return null;
    const primaryOptions = visibleRouteGroups.map((g) => g.primary);
    return groupOptionsByProvider(primaryOptions, preferredProviderId);
  }, [flatRecommended, visibleRouteGroups, preferredProviderId]);

  const routeGroupByPrimaryId = useMemo(() => {
    const map = new Map<string, ModelRouteGroup>();
    for (const g of visibleRouteGroups) map.set(g.primary.id, g);
    return map;
  }, [visibleRouteGroups]);

  const showCustom =
    allowCustom &&
    normalizedQuery.length > 0 &&
    !catalogOptions.some((o) => o.id === normalizedQuery) &&
    !options.some((o) => o.id === normalizedQuery);

  const hasModels = catalogOptions.length > 0;
  const hiddenCount = hasRecommended ? catalogRouteGroups.length - defaultRouteGroups.length : 0;
  const showExpandRow = hasRecommended && !showAll && !searching && hiddenCount > 0;

  function commit(next: string | undefined) {
    onChange(next);
    setQuery('');
  }

  const stacked = Boolean(leadingSection || trailingSection);

  function renderRouteGroup(group: ModelRouteGroup) {
    return (
      <ModelRouteRow
        key={group.key}
        group={group}
        value={value}
        onSelect={commit}
        disabled={disabled}
      />
    );
  }

  return (
    <div
      className={cn(
        stacked && 'divide-y divide-border',
        fillAvailable && 'flex min-h-0 flex-1 flex-col overflow-hidden',
      )}
    >
      {leadingSection}
      <Command shouldFilter={false} className={fillAvailable ? 'h-auto min-h-0 flex-1' : undefined}>
        <CommandInput
          placeholder={allowCustom ? 'Search or type a model…' : searchPlaceholder}
          value={query}
          onValueChange={setQuery}
          className="h-9"
          disabled={disabled}
        />
        <CommandList
          className={
            fillAvailable ? 'max-h-none min-h-0 flex-1' : compactList ? 'max-h-72' : 'max-h-[360px]'
          }
          data-tutorial-id={listTutorialId}
          data-testid="model-picker-list"
        >
          {!hasModels ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-muted-foreground">No models available</p>
              {emptyCta}
            </div>
          ) : null}
          {hasModels && visibleRouteGroups.length === 0 && !showCustom ? (
            <CommandEmpty>No models found.</CommandEmpty>
          ) : null}
          {allowInherit ? (
            <CommandGroup>
              <CommandItem
                value="__default__"
                disabled={disabled}
                onSelect={() => commit(undefined)}
                data-testid="model-combobox-default"
                className={pickerItemClass(undefined, !value)}
              >
                <span className="flex-1 truncate text-muted-foreground">{defaultLabel}</span>
                {!value ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" /> : null}
              </CommandItem>
            </CommandGroup>
          ) : null}
          {flatRecommended ? (
            <CommandGroup data-testid="model-picker-flat-group">
              {visibleRouteGroups.map(renderRouteGroup)}
            </CommandGroup>
          ) : (
            groups?.map((group) => (
              <CommandGroup
                key={group.provider}
                heading={group.label}
                className={GROUP_HEADING_CLASS}
                data-testid={`model-picker-provider-${group.provider}`}
              >
                {group.options.map((option) => {
                  const routeGroup = routeGroupByPrimaryId.get(option.id);
                  if (!routeGroup) return null;
                  return renderRouteGroup(routeGroup);
                })}
              </CommandGroup>
            ))
          )}
          {showExpandRow ? (
            <CommandGroup>
              <CommandItem
                value="__show_all__"
                disabled={disabled}
                onSelect={() => setShowAll(true)}
                data-testid="model-picker-show-all"
                className={pickerItemClass(undefined, false, 'text-muted-foreground')}
              >
                <span className="flex-1 truncate">Show all {catalogRouteGroups.length} models</span>
              </CommandItem>
            </CommandGroup>
          ) : null}
          {showCustom ? (
            <>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  value={`__custom__ ${normalizedQuery}`}
                  disabled={disabled}
                  onSelect={() => commit(normalizedQuery)}
                  data-testid="model-combobox-custom"
                  className={pickerItemClass(getModelIconConfig(normalizedQuery).bgColor, false)}
                >
                  <ModelProviderIcon model={normalizedQuery} />
                  <span className="flex-1 truncate">
                    Use <span className="font-mono">{normalizedQuery}</span>
                  </span>
                </CommandItem>
              </CommandGroup>
            </>
          ) : null}
        </CommandList>
        {footer && hasModels ? (
          <>
            <CommandSeparator />
            <div className="shrink-0 px-3 py-1.5">{footer}</div>
          </>
        ) : null}
      </Command>
      {trailingSection}
    </div>
  );
}

/**
 * Shared model picker: Popover + Command with chat-style rich rows (provider
 * groups, brand icon, description, badge, selected check). Form mode adds
 * inherit + free-form typed id; chat shells pass compact/header triggers and a
 * manage models footer.
 */
export function ModelPicker({
  value,
  onChange,
  options = DEFAULT_OPTIONS,
  enabledModelIds,
  allowInherit = false,
  allowCustom = false,
  placeholder = 'Default model',
  defaultLabel = 'Default (inherit)',
  searchPlaceholder = 'Search models…',
  triggerVariant = 'form',
  footer,
  emptyCta,
  preferredProviderId,
  providerOrder,
  leadingSection,
  trailingSection,
  onClose,
  disabled,
  className,
  'data-testid': dataTestId,
  id,
  listTutorialId,
  compactList,
}: ModelPickerProps) {
  const [open, setOpen] = useState(false);

  const listIsCompact = compactList ?? triggerVariant === 'form';

  const selectedOption = useMemo(() => options.find((o) => o.id === value), [options, value]);
  const catalogName = value ? resolveConfig(value)?.name : undefined;
  const selectedLabel = selectedOption?.name ?? catalogName ?? value;

  const isForm = triggerVariant === 'form';
  const isCompact = triggerVariant === 'compact' || triggerVariant === 'compact-ghost';
  const isGhost = triggerVariant === 'compact-ghost';

  function handleChange(next: string | undefined) {
    onChange(next);
    setOpen(false);
    onClose?.();
  }

  const list = (
    <ModelPickerList
      value={value}
      onChange={handleChange}
      options={options}
      enabledModelIds={enabledModelIds}
      allowInherit={allowInherit}
      allowCustom={allowCustom}
      defaultLabel={defaultLabel}
      searchPlaceholder={searchPlaceholder}
      footer={footer}
      emptyCta={emptyCta}
      preferredProviderId={preferredProviderId}
      providerOrder={providerOrder}
      leadingSection={leadingSection}
      trailingSection={trailingSection}
      compactList={listIsCompact}
      disabled={disabled}
      listTutorialId={listTutorialId}
      resetKey={open}
    />
  );

  const triggerSize = isForm ? 'form' : isCompact ? 'compact' : 'header';
  const triggerAriaLabel =
    value && selectedLabel ? `Select model: ${selectedLabel}` : 'Select model';

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        {isForm ? (
          <Button
            type="button"
            id={id}
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={open}
            aria-label={triggerAriaLabel}
            disabled={disabled}
            data-testid={dataTestId ?? 'model-combobox'}
            className={cn('h-8 w-full justify-start gap-1.5 px-2 text-xs font-normal', className)}
          >
            <ModelPickerTriggerContent
              value={value}
              label={selectedLabel}
              placeholder={placeholder}
              size={triggerSize}
            />
          </Button>
        ) : isCompact ? (
          <Button
            type="button"
            id={id}
            variant={isGhost ? 'ghost' : 'outline'}
            size="sm"
            role="combobox"
            aria-expanded={open}
            aria-label={triggerAriaLabel}
            disabled={disabled}
            data-testid={dataTestId ?? 'model-selector'}
            className={cn(
              'group h-7 max-w-60 justify-start gap-1.5 px-2 text-xs font-normal',
              className,
            )}
          >
            <ModelPickerTriggerContent
              value={value}
              label={selectedLabel}
              placeholder={placeholder}
              size={triggerSize}
            />
          </Button>
        ) : (
          <Button
            type="button"
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={triggerAriaLabel}
            disabled={disabled}
            data-testid={dataTestId ?? 'model-selector'}
            className={cn(
              'group h-8 gap-2 rounded-lg border-border/60 bg-card px-3 text-sm font-normal shadow-none',
              'hover:border-border hover:bg-accent/50 hover:shadow-sm',
              'dark:border-border/60 dark:bg-card dark:hover:bg-accent/50',
              className,
            )}
          >
            <ModelPickerTriggerContent
              value={value}
              label={selectedLabel}
              placeholder={placeholder}
              size={triggerSize}
            />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', isForm ? 'w-72' : 'w-80')}
        align="start"
        data-testid="model-picker-content"
      >
        {list}
      </PopoverContent>
    </Popover>
  );
}
